import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import logo from "@/assets/brand/ketosource-logo-full-purple.svg";
import { SHELL_PATH, probeSession, resolveDoorState } from "@/lib/session";
import type { DoorState } from "@/lib/session";

/**
 * S1 · Door — the single sign-in for the whole estate.
 *
 * One action on the screen, because the screen exists to be left (UX v0.3 W1).
 * All five S1 states render from `resolveDoorState`; none of them is a dead end
 * — every error names a person or a system and a next step (UX v0.3 §7).
 */
export function DoorPage() {
  const probe = useQuery({
    queryKey: ["session"],
    queryFn: ({ signal }) => probeSession(signal),
    retry: false,
    // The Access cookie can change out from under a long-open tab; the door
    // is cheap and must never show a stale "signed in" that no longer holds.
    staleTime: 0,
  });

  const [signingIn, setSigningIn] = useState(false);

  const state: DoorState = probe.isPending
    ? "loading"
    : signingIn
      ? "loading"
      : resolveDoorState(
          probe.data ?? {
            kind: "refused",
            refusal: { status: 0, reason: "unreachable", message: "" },
          },
        );

  // Edge — already signed in: skip the door entirely and go to the shell.
  useEffect(() => {
    if (state === "signed-in") window.location.assign(SHELL_PATH);
  }, [state]);

  const signIn = () => {
    setSigningIn(true);
    // Sign-in is a navigation into the Access-protected path, not a fetch:
    // Cloudflare Access runs the OIDC redirect and returns with the cookie set.
    window.location.assign(SHELL_PATH);
  };

  return (
    <main className="flex min-h-dvh items-center justify-center bg-ks-purple-4 p-8">
      <div className="w-full max-w-md rounded-ks-md border border-ks-grey-4 bg-ks-surface px-8 py-12 text-center">
        {/* Full logo at 220px — the brand minimum is 150px (UX v0.3 §8). */}
        <img src={logo} alt="Ketosource" className="mx-auto mb-12 h-auto w-[220px]" />

        <h1 className="mb-2 text-3xl font-bold text-ks-purple">Ketosource OS</h1>
        {/* Hierarchy is size + weight, not a lighter grey: the brand's only
            secondary grey (--ks-grey-1) is 3.15:1 on white, under the
            4.5:1 WCAG 2.1 SC 1.4.3 minimum for normal text. */}
        <p className="mb-8 text-ks-ink">Everything in one place, your area first</p>

        {state === "error-account" && (
          <Alert variant="destructive" className="mb-6 text-left">
            <AlertTitle>That account isn&rsquo;t a Ketosource account.</AlertTitle>
            <AlertDescription>Ask Damien for access.</AlertDescription>
          </Alert>
        )}

        {state === "error-provider" && (
          <Alert variant="destructive" className="mb-6 text-left">
            <AlertTitle>Sign-in is unavailable right now</AlertTitle>
            <AlertDescription>
              The identity provider isn&rsquo;t responding. Zach is the contact while it&rsquo;s
              down.
            </AlertDescription>
          </Alert>
        )}

        {/* The button holds its size across every state so nothing reflows
            when an alert appears above it (UX S1 loading: no layout shift). */}
        {state === "loading" ? (
          <Button size="lg" className="w-full" disabled aria-busy="true">
            Signing you in&hellip;
          </Button>
        ) : state === "error-provider" ? (
          <Button size="lg" className="w-full" onClick={() => void probe.refetch()}>
            Try again
          </Button>
        ) : (
          <Button size="lg" className="w-full" onClick={signIn}>
            Sign in with Ketosource
          </Button>
        )}

        <p className="mt-6 text-sm text-ks-ink">Not a Ketosource account? Ask Damien.</p>
      </div>
    </main>
  );
}
